import { useId, type ReactNode } from 'react'
import type {
  Claim,
  DraftSignal,
  Evidence,
  IdentityGraph,
  LedgerItem,
  Modality,
  Party,
  ReplyOwedSignal,
} from '@cordelia/core'
import { cx } from '../cx.js'
import { shortDate } from '../dates.js'
import { partyName } from '../partyName.js'
import styles from './Card.module.scss'

/**
 * Card — one ledger item (M3.3), in the anatomy the v1 Spec fixes (*Card
 * anatomy*; build rule 4): the proposition; the counterparty; personal or on
 * behalf of an organization; timing, condition and modality as written; why
 * it appears now; one evidence excerpt with speaker, state and source time;
 * the signal row; then the section's actions, Trace, and a footer slot.
 *
 * The card renders what it is given and derives nothing: no timing the
 * source did not state, no meter, no percentage, no colour that encodes a
 * judgment. Evidence is the speaker's own words — serif, typographic
 * quotation marks, a solid ink rule, attributed beneath. A signal is the
 * product's voice — sans, a dashed hairline in a lighter grey, a small
 * label, never quoted (docs/visual-direction.md, "Evidence and signals are
 * different things"). The article is named by its proposition and focusable
 * by script, so a skip link can land on it; Trace opens from a button, never
 * from a click on the card.
 */
export type CardLinks = {
  /** Where the source of a piece of evidence opens (the thread in Gmail); null for none. */
  readonly source?: ((evidence: Evidence) => string | null) | undefined
  /** Where an unsent draft can be resumed; null for none. */
  readonly draft?: ((signal: DraftSignal) => string | null) | undefined
}

export type CardProps = {
  readonly item: LedgerItem
  /** The head claim: modality, timing and condition as written. */
  readonly claim?: Claim | undefined
  /** The item's evidence, in order. The first is the excerpt. */
  readonly evidence?: readonly Evidence[] | undefined
  readonly drafts?: readonly DraftSignal[] | undefined
  readonly replies?: readonly ReplyOwedSignal[] | undefined
  /** Why the item appears now: the attention reasons to show, in words. */
  readonly whyNow?: readonly string[] | undefined
  /** Names for the parties on the card. */
  readonly identity: IdentityGraph
  readonly links?: CardLinks | undefined
  /** DOM id, so a skip link can reach the card. */
  readonly id?: string | undefined
  /** The section's actions (M3.6b): real buttons, the smallest useful set. */
  readonly actions?: ReactNode
  /** The Trace trigger (M3.5), last in the action row; the inline Trace until the sheet lands. */
  readonly trace?: ReactNode
  /** Trace is open for this card. */
  readonly expanded?: boolean | undefined
  /** After Trace: study mode's scoring fieldset (M3.10). */
  readonly children?: ReactNode
}

/** The force the speaker used, as the modality field preserved it. */
const MODALITY_WORDS: Readonly<Record<Modality, string>> = {
  will: 'will',
  try: 'will try to',
  intend: 'intends to',
  may: 'might',
  request: 'asked',
  question: 'asked',
  other: 'said',
}

export function Card({
  item,
  claim,
  evidence = [],
  drafts = [],
  replies = [],
  whyNow = [],
  identity,
  links,
  id,
  actions,
  trace,
  expanded = false,
  children,
}: CardProps) {
  const headingId = useId()
  const name = (party: Party | 'system'): string =>
    party === 'system' ? 'the system' : partyName(party, identity)
  // Organizational and collective cards share the variant: neither is the user alone.
  const isOrganizational =
    item.promise_scope === 'organizational' || item.promise_scope === 'collective'
  const scope =
    item.promise_scope === 'organizational'
      ? `on behalf of ${name(item.owed_by)}`
      : item.promise_scope
  const force = claim
    ? [MODALITY_WORDS[claim.modality], claim.timing_text, claim.condition]
        .filter((part): part is string => typeof part === 'string' && part.length > 0)
        .join(' · ')
    : ''
  const excerpt = evidence[0]
  const sourceHref = excerpt ? (links?.source?.(excerpt) ?? null) : null

  return (
    <article
      id={id}
      // Focusable by script only, for the skip link; never in the tab order.
      tabIndex={-1}
      className={cx(
        styles.root,
        isOrganizational && styles.isOrganizational,
        expanded && styles.isExpanded,
      )}
      aria-labelledby={headingId}
      data-item={item.id}
      data-section={item.surface}
      data-cohort={item.cohort}
    >
      <h3 id={headingId} className={styles.proposition}>
        {item.proposition}
      </h3>
      <ScopeLine item={item} identity={identity} />
      <ul className={styles.meta}>
        <li>{name(item.counterparty)}</li>
        <li>
          <span className={styles.badge}>{scope}</span>
        </li>
        {force ? <li>{force}</li> : null}
      </ul>
      {whyNow.length > 0 ? (
        <ul className={styles.whyNow}>
          {whyNow.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
      {excerpt ? (
        <figure className={styles.evidence}>
          <blockquote className={styles.quote}>“{excerpt.exact_quote}”</blockquote>
          <figcaption className={styles.attribution}>
            {name(excerpt.speaker)} · {excerpt.source_state} · {shortDate(excerpt.timestamp)}
            {sourceHref ? (
              <>
                {' · '}
                <a href={sourceHref} target="_blank" rel="noopener">
                  Open in Gmail
                </a>
              </>
            ) : null}
          </figcaption>
        </figure>
      ) : null}
      {replies.length > 0 || drafts.length > 0 ? (
        <ul className={styles.signals}>
          {replies.map((reply) => (
            <li key={reply.id} className={styles.signal}>
              <span className={styles.signalLabel}>Signal</span>
              {name(reply.counterparty)} asked on {shortDate(reply.asked_at)} and hasn't heard back.
            </li>
          ))}
          {drafts.map((draft) => {
            const href = links?.draft?.(draft) ?? null
            return (
              <li key={draft.id} className={styles.signal}>
                <span className={styles.signalLabel}>Signal · unsent draft</span>
                You started a reply on {shortDate(draft.created_at)} and never sent it.
                {href ? (
                  <>
                    {' '}
                    <a href={href} target="_blank" rel="noopener">
                      Continue draft
                    </a>
                  </>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
      {actions || trace ? (
        <div className={styles.actions}>
          {actions}
          {trace}
        </div>
      ) : null}
      {children}
    </article>
  )
}

/**
 * The user's part, in the product's voice, when the card is not simply
 * personal (Constitution, *Personal and organizational commitments*; the
 * strings are in docs/copy.md). The organization is the subject and the user
 * is the one who made the promise, so sole personal liability is never
 * implied. On May be expected — a role or an obligor unresolved — the
 * organization only *may* owe it, and the card says why: the user said "we".
 * Who is positioned to act appears only when it is established; unresolved
 * is the absence of the line, not a warning.
 */
function ScopeLine({ item, identity }: { item: LedgerItem; identity: IdentityGraph }) {
  const scope = item.promise_scope
  if (scope === 'personal') return null
  if (scope === 'uncertain')
    return item.surface === 'may-be-expected' ? (
      <p className={styles.scopeLine}>Who owes this is not established.</p>
    ) : null

  const organization = organizationOf(item, identity)
  const hedged = item.surface === 'may-be-expected'
  // The proposition names the organization as its subject; do not say it twice.
  const named = organization !== null && item.proposition.startsWith(organization)
  const actor =
    item.responsible_actor === 'unresolved'
      ? null
      : item.responsible_actor === identity.user
        ? 'you'
        : partyName(item.responsible_actor, identity)
  return (
    <p className={styles.scopeLine}>
      {hedged ? (
        <>
          {organization ? (
            <strong>{organization} may owe this.</strong>
          ) : (
            'Who owes this is not established.'
          )}{' '}
          You said “we”.
        </>
      ) : (
        <>
          {organization && !named ? (
            <>
              <strong>{organization} owes this.</strong>{' '}
            </>
          ) : null}
          You made this promise.
        </>
      )}
      {actor ? ` Currently with: ${actor}.` : null}
    </p>
  )
}

/**
 * The organization behind a "we": the obligor when it is resolved to one,
 * else the organization the role diagnostic names, else none.
 */
function organizationOf(item: LedgerItem, identity: IdentityGraph): string | null {
  const isOrganization = (party: Party): boolean =>
    identity.organizations.some((o) => o.id === party)
  if (item.owed_by !== 'unresolved' && isOrganization(item.owed_by))
    return partyName(item.owed_by, identity)
  for (const d of item.diagnostics)
    if (d.kind === 'role_unresolved' && d.organization) return partyName(d.organization, identity)
  return null
}
